import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch } from '@/lib/hooks';
import { setModalName } from '@/lib/slices/modal-slice';

import FormModal from '@/common/components/modals/general/FormModal';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputTextarea from '@/common/components/input/StandardInputTextarea';
import StandardInputNumber from '@/common/components/input/StandardInputNumber';
import { AIIcon } from '@/assets/icons/button';
import ToggleSelect from '@/common/components/input/SegmentedToggle';
import StandardInputFile from '@/common/components/input/StandardInputFile';

import Request from '@/services/util/request-service';
import { CourseBuilderData } from 'course-types';

interface Props {
  setCourseBuilderData: (data: CourseBuilderData) => void;
}

function CourseGenModal({ setCourseBuilderData }: Props) {
  const dispatch = useAppDispatch();

  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [mode, setMode] = useState<'prompt' | 'pdf'>('prompt');

  // Prompt parameters
  const [courseGenName, setCourseGenName] = useState<string>('');
  const [courseDesc, setCourseDesc] = useState<string>('');
  const [courseDifficulty, setCourseDifficulty] = useState<string>('');
  const [unitCount, setUnitCount] = useState<number | undefined>(undefined);
  const [courseLength, setCourseLength] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

  // Send parameters to aurora and retrieve generated layout
  const continueCourseAutoGen = async () => {
    // Send prompt parameters to aurora
    // TODO:: replace TITLE with NAME
    const res = await Request.post(
      'course/layout',
      {
        Title: courseGenName,
        Details: courseDesc,
        Difficulty: courseDifficulty,
        Units: unitCount,
        Length: courseLength,
      },
      true,
    );
    const body = await res?.json();

    // Handle errors with request / response
    if (!res || !body) {
      courseGenError();
      throw new Error('Request failed');
    }

    // Validate data structure in response, throw error if invalid
    if (!checkIfValidData(body)) {
      courseGenError(true);
      throw new Error('Invalid response data');
    }

    // Update course layout data, return to wizard, reset component state
    setCourseBuilderData({ name: body?.name?.trim() ?? courseGenName.trim(), ...body });
    resetCourseGenState();
    dispatch(setModalName('CreateCourse'));
    throw new Error('SKIP_CLOSE');
  };

  // Send PDF + params to aurora and retrieve generated layout (temporary testing mode)
  const continueCourseDocGen = async () => {
    if (!selectedFile) {
      toast.error('Please attach a PDF file.');
      throw new Error('Missing PDF');
    }

    const form = new FormData();
    form.append('doc', selectedFile);
    form.append('title', courseGenName);
    if (courseDifficulty.trim()) form.append('difficulty', courseDifficulty);
    if (unitCount !== undefined) form.append('units', String(unitCount));
    if (courseLength.trim()) form.append('length', courseLength);

    const res = await Request.post('course/layout/doc', form, true);
    const body = await res?.json();

    if (!res || !body) {
      courseGenError();
      throw new Error('Request failed');
    }

    if (!checkIfValidData(body)) {
      courseGenError(true);
      throw new Error('Invalid response data');
    }

    setCourseBuilderData({ name: body?.name?.trim() ?? courseGenName.trim(), ...body });
    resetCourseGenState();
    dispatch(setModalName('CreateCourse'));
    throw new Error('SKIP_CLOSE');
  };

  // Error handler
  const courseGenError = (invalidJson: boolean = false) => {
    if (invalidJson) {
      toast.error('Something went wrong during generation, please try again');
      return;
    }
    toast.error('Error while communicating with the server, please try again later');
  };

  // Clear current component state data
  const resetCourseGenState = () => {
    setShowAdvanced(false);
    setMode('prompt');
    setCourseGenName('');
    setCourseDesc('');
    setCourseDifficulty('');
    setUnitCount(undefined);
    setCourseLength('');
    setSelectedFile(undefined);
  };

  // Return to course creator wizard
  const handleClose = () => {
    dispatch(setModalName('CreateCourse'));
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    if (mode === 'prompt') {
      return (
        courseGenName.trim() !== '' && courseDesc.trim() !== '' && courseDifficulty.trim() !== ''
      );
    }
    // pdf mode: require title and file only
    return courseGenName.trim() !== '' && Boolean(selectedFile);
  };

  const checkIfValidData = (data: any): boolean => {
    // Basic checks
    if (!data || typeof data !== 'object') return false;
    if (typeof data.name !== 'string') return false;
    if (!Array.isArray(data.units)) return false;

    // Each unit in units:
    for (const unit of data.units) {
      if (!unit || typeof unit !== 'object') return false;
      if (typeof unit.name !== 'string') return false;
      if (!Array.isArray(unit.subunits)) return false;

      // unit.description is optional, but if present must be string
      if (unit.description && typeof unit.description !== 'string') return false;

      // Each subunit
      for (const sub of unit.subunits) {
        if (!sub || typeof sub !== 'object') return false;
        if (typeof sub.name !== 'string') return false;
        if (!Array.isArray(sub.activities)) return false;
      }
    }

    // all checks passed
    return true;
  };

  const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.trim();

    // If the user clears the field, set undefined
    if (rawValue === '') {
      setUnitCount(undefined);
      return;
    }

    // Attempt to parse as integer
    const parsed = parseInt(rawValue, 10);

    // Check validity (not NaN, in [1..20])
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      setUnitCount(parsed);
    }
  };

  const handleSubmit = () => (mode === 'prompt' ? continueCourseAutoGen() : continueCourseDocGen());

  return (
    <FormModal
      modalName='GenerateCourse'
      title='Generate New Course'
      subtitle='Fill the fields with the course information'
      className='max-w-[900px] w-full'
      closeOnClickOff={false}
      onSubmit={handleSubmit}
      submitButton={{
        text: 'Generate course',
        icon: <AIIcon className='text-text_contrast w-[20px] h-[20px] mr-2' />,
        disabled: !isFormValid(),
      }}
      loadingText='Generating your course...'
      advancedOptions={{
        content: (
          <>
            <StandardInputNumber
              id='unitCount'
              label='Unit count'
              isRequired={false}
              placeholderText='Enter number of units (max of 10)'
              min={1}
              max={10}
              value={unitCount !== undefined ? unitCount : ''}
              onChange={handleUnitCountChange}
            />
            <StandardInputText
              id='courseLength'
              label='Course length'
              isRequired={false}
              placeholderText='Example: 1 semester, 2 semesters...'
              value={courseLength}
              onChange={(e) => setCourseLength(e.target.value)}
            />
          </>
        ),
        toggleText: 'Advanced options',
        sectionClassName:
          'flex flex-col gap-[16px] w-full bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300',
        externalState: [showAdvanced, setShowAdvanced],
      }}
    >
      {/* Temporary Mode Switch */}
      <div className='mb-3'>
        <ToggleSelect
          className='max-w-xs'
          options={[
            { key: 'prompt', label: 'Prompt' },
            { key: 'pdf', label: 'Document' },
          ]}
          activeOption={mode}
          onSwitch={(k) => setMode(k as 'prompt' | 'pdf')}
        />
      </div>

      {mode === 'prompt' ? (
        <>
          <StandardInputText
            id='courseName'
            label='Course name'
            isRequired={true}
            placeholderText='Enter course name'
            value={courseGenName}
            onChange={(e) => setCourseGenName(e.target.value)}
          />
          <StandardInputTextarea
            id='courseDetails'
            label='Details / Instructions'
            isRequired={true}
            placeholderText='Enter details or a description that will be useful in building the course'
            rows={showAdvanced ? 3 : 11}
            value={courseDesc}
            onChange={(e) => setCourseDesc(e.target.value)}
          />
          <StandardInputText
            id='courseDifficulty'
            label='Difficulty level / Target audience'
            isRequired={true}
            placeholderText='Example: 9th grade, College level, Working professional...'
            value={courseDifficulty}
            onChange={(e) => setCourseDifficulty(e.target.value)}
          />
        </>
      ) : (
        <>
          <StandardInputText
            id='courseName'
            label='Course name'
            isRequired={true}
            placeholderText='Enter course name'
            value={courseGenName}
            onChange={(e) => setCourseGenName(e.target.value)}
          />
          <StandardInputText
            id='courseDifficulty'
            label='Difficulty level / Target audience (optional)'
            isRequired={false}
            placeholderText='Example: 9th grade, College level, Working professional...'
            value={courseDifficulty}
            onChange={(e) => setCourseDifficulty(e.target.value)}
          />
          <StandardInputFile
            id='courseDoc'
            label='PDF Document'
            isRequired={true}
            placeholderText='Attach a PDF document'
            accept='.pdf,application/pdf'
            onChange={(e) => setSelectedFile(e.target.files?.[0])}
          />
        </>
      )}
    </FormModal>
  );
}

export default CourseGenModal;
